import {
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { ChannelSheetService } from '@service/channel/channel-sheet.service';
import { ChannelService } from '@service/channel/channel.service';
import { Channel } from 'icreate/common/models/channel';
import { ChannelGroup } from 'icreate/common/models/channel-group';
import { Subscription, lastValueFrom } from 'rxjs';
import { ConstMotorChannelType, ConstStatusText } from '@models/ams-channel-const-name-list';
import { ChannelSheetCol, dropdownSources, motorRunDefinitions } from '@models/channel-sheet';
import {
    ChannelType,
    ChannelTypeStringLookup,
    MotorChannelType,
    MotorTypeStrings,
    getAnalogTypesSet,
    getFlowTypesSet,
    getRunningTypesSet,
    isBlank,
    isSystemChannel,
} from 'icreate/common/models/channel-type';
import { InputValueState } from '../channel-text-input/channel-text-input.component';
import { AlarmPropertyList } from 'icreate/common/models/repose-group';
import _ from 'lodash';
import { VesselService } from '@service/vessel.service';
import { parseExcelRow, truncateToDecimals } from '@service/channel/channel-utils';
import { ChannelPanelAlarmContactComponent } from '../channel-panel-alarm-contact/channel-panel-alarm-contact.component';
import { TranslateService } from '@ngx-translate/core';
import { AlarmSettingFns } from 'icreate/common/models/alarm-setting';

export enum StatusSetting {
    PRESET = 'preset',
    CUSTOM = 'custom',
}

@Component({
    selector: 'app-channel-panel-alarm',
    templateUrl: './channel-panel-alarm.component.html',
    styleUrls: ['./channel-panel-alarm.component.scss'],
    standalone: false,
})
export class ChannelPanelAlarmComponent implements OnChanges, OnInit, OnDestroy {
    readonly CHANNEL_TYPE_STRINGS = dropdownSources.channelType;
    readonly CONST_DI_STATUS_TEXT = [...ConstStatusText.map(status => status.text)];
    readonly CONST_DM_STATUS_TEXT = [...ConstMotorChannelType.map(status => status.text)];
    readonly StatusSetting = StatusSetting;

    @Input() selectedChannel: any;
    @Input() selectedChannelId: string;
    @Input() activeGroup: ChannelGroup;
    @Input() canEditSheet: boolean;

    @ViewChild(ChannelPanelAlarmContactComponent)
    contactComponent: ChannelPanelAlarmContactComponent;

    newChannel: Channel;
    isAnalog: boolean;
    isRunning: boolean;
    isFlow: boolean;
    isExhGasDev: boolean = false;
    isExhGasMean: boolean = false;
    isDigitalContact: boolean = false;
    isDigitalMotor: boolean = false;
    isSystemChannel: boolean = false;
    isSheetLoading: boolean = false;
    isSaveOn: boolean;
    savedChannel: Channel;

    initialChannelValues: Channel;

    updateChannelSubscription: Subscription;
    channelsSavedSubscription: Subscription;
    saveStartedSubscription: Subscription;
    isNormalRangeEnabled: boolean = true;
    isShowSelect = true;
    statusSetting = StatusSetting.PRESET;

    measurableRangeMinString: string = '';
    measurableRangeMaxString: string = '';
    normalRangeMinString: string = '';
    normalRangeMaxString: string = '';

    private savedNormalRange: any;
    private isEditingRange = false;

    get fullScaleRangeDisplay(): string {
        if (this.isRunning) {
            return this.translateService.instant('channel.setting.value.full-scale-range.running-hour');
        }
        return this.translateService.instant('channel.setting.value.full-scale-range');
    }

    constructor(
        private cdr: ChangeDetectorRef,
        private channelSheetService: ChannelSheetService,
        private channelService: ChannelService,
        private vesselService: VesselService,
        private translateService: TranslateService
    ) {}

    async ngOnInit() {
        this.updateChannelSubscription = this.channelSheetService.updateChannel$.subscribe(updatedChannel => {
            if (this.isEditingRange) return;
            if (!updatedChannel || _.isEqual(updatedChannel, this.selectedChannel)) return;
            if (_.isEqual(updatedChannel, this.newChannel)) return;
            if (!updatedChannel.rowId) {
                updatedChannel = parseExcelRow(updatedChannel);
            }
            if (updatedChannel.rowId === this.selectedChannel.rowId) {
                this.isSheetLoading = true;
                this.selectedChannel = updatedChannel;
                this.updateChannelTypeFlags(this.selectedChannel);
                this.newChannel = _.cloneDeep(this.selectedChannel);
                if (
                    !Number.isNaN(this.newChannel.fcuData.normalRange?.min) ||
                    !Number.isNaN(this.newChannel.fcuData.normalRange?.max)
                ) {
                    this.isNormalRangeEnabled = true;
                }
                this.newChannel.fcuData.scale = this.computeMeasurableScale();
                this.syncRangeStrings();
                this.isSheetLoading = false;
            }
        });
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (changes.selectedChannelId && this.selectedChannelId) {
            this.isSaveOn = this.channelSheetService.getIsChannelUpdated();
            await this.channelSelect();
        } else {
            this.selectedChannel = undefined;
            this.newChannel = undefined;
        }
    }

    ngOnDestroy(): void {
        this.updateChannelSubscription?.unsubscribe();
        this.channelsSavedSubscription?.unsubscribe();
        this.saveStartedSubscription?.unsubscribe();
    }

    private formatWithScale(value: number, scale: number): string {
        if (value == null || Number.isNaN(value)) return '';
        const decimals = scale <= 1 ? 0 : Math.round(Math.log10(scale));
        return truncateToDecimals(value, decimals);
    }

    private computeMeasurableScale(): number {
        const minString = this.measurableRangeMinString ?? '';
        const maxString = this.measurableRangeMaxString ?? '';
        const minDecimals = this.countDecimals(minString);
        const maxDecimals = this.countDecimals(maxString);
        const newDecimals = Math.max(minDecimals, maxDecimals);
        return newDecimals === 0 ? 1 : Math.pow(10, newDecimals);
    }

    private syncRangeStrings() {
        const measurableRange = this.newChannel?.fcuData?.measurableRange;
        const normalRange = this.newChannel?.fcuData?.normalRange;
        const scale = this.newChannel?.fcuData?.scale || 1;

        if (measurableRange) {
            this.measurableRangeMinString = this.formatWithScale(measurableRange.min, scale);
            this.measurableRangeMaxString = this.formatWithScale(measurableRange.max, scale);
        }
        if (normalRange) {
            this.normalRangeMinString = this.formatWithScale(normalRange.min, scale);
            this.normalRangeMaxString = this.formatWithScale(normalRange.max, scale);
        }
    }

    async channelSelect() {
        this.isSheetLoading = true;
        if (this.activeGroup && this.selectedChannel) {
            this.savedChannel = await lastValueFrom(
                this.channelService.getChannel(
                    this.vesselService.getActiveVesselId(),
                    this.selectedChannelId,
                    this.activeGroup.id
                )
            );

            this.updateChannelTypeFlags(this.selectedChannel);
            this.newChannel = _.cloneDeep(this.selectedChannel);
            this.initialChannelValues = _.cloneDeep(this.selectedChannel);
            if (this.isRunning) {
                this.newChannel.fcuData.normalRange = { min: NaN, max: NaN };
                this.initialChannelValues.fcuData.normalRange = { min: NaN, max: NaN };
                this.isNormalRangeEnabled = false;
            } else {
                this.isNormalRangeEnabled = true;
            }
            this.syncRangeStrings();

            this.channelSheetService.setIsAlarmUpdate(!_.isEqual(this.initialChannelValues, this.newChannel));
        }
        this.contactComponent?.resetTouched();
        this.isSheetLoading = false;
    }

    onInputChange(event: { name: string; value: any; property: string }) {
        const newChannel = _.cloneDeep(this.newChannel);
        let isChannelTypeChanged = false;
        if (event.name === 'opsDataChannelType') {
            const prevChannelType = newChannel.fcuData.channelType;
            newChannel.fcuData.channelType = ChannelTypeStringLookup[event.value];
            newChannel.opsData.channelType = event.value;

            if (prevChannelType === ChannelType.BLANK && newChannel.fcuData.channelType !== ChannelType.BLANK) {
                if (
                    !newChannel.opsData.tag ||
                    newChannel.opsData.tag.trim() === '' ||
                    newChannel.opsData.tag.includes('NaN')
                ) {
                    const tagNum = (typeof newChannel.rowIndex === 'number' ? newChannel.rowIndex : 0) + 1;
                    newChannel.opsData.tag = `${String(tagNum).padStart(4, '0')}`;
                }
                if (!newChannel.opsData.name || newChannel.opsData.name.trim() === '') {
                    newChannel.opsData.name = event.value;
                }
            }
            isChannelTypeChanged = true;
        } else if (event.name === 'status') {
            newChannel.fcuData.digitalAlarmSetting.statusString = event.value;
            newChannel.fcuData.digitalAlarmSetting.statusStringContactOff = event.value.split('/')[1];
            newChannel.fcuData.digitalAlarmSetting.statusStringContactOn = event.value.split('/')[0];
            newChannel.fcuData.alarmSettingVeryHigh.statusString = event.value;
            newChannel.fcuData.alarmSettingHigh.statusString = event.value;
            newChannel.fcuData.alarmSettingLow.statusString = event.value;
            newChannel.fcuData.alarmSettingVeryLow.statusString = event.value;
        } else if (event.name === 'motorStatus') {
            if (newChannel.fcuData.motorChannelSetting) {
                newChannel.fcuData.motorChannelSetting.motorType = event.value;

                const motorTypeEntry = Object.entries(MotorTypeStrings).find(([, str]) => str === event.value);
                if (motorTypeEntry) {
                    const enumVal = Number(motorTypeEntry[0]) as MotorChannelType;
                    const definition = motorRunDefinitions.find(([type]) => type === enumVal);
                    if (definition) {
                        const defaults = definition[1];
                        newChannel.fcuData.motorChannelSetting.stopStatusString =
                            defaults[ChannelSheetCol.STOP_STATUS] ?? '';
                        newChannel.fcuData.motorChannelSetting.stbyStatusString =
                            defaults[ChannelSheetCol.STBY_STATUS] ?? '';
                        newChannel.fcuData.motorChannelSetting.runStatusString1 =
                            defaults[ChannelSheetCol.RUN1_STATUS] ?? '';
                        newChannel.fcuData.motorChannelSetting.runStatusString2 =
                            defaults[ChannelSheetCol.RUN2_STATUS] ?? '';
                        newChannel.fcuData.motorChannelSetting.runStatusString3 =
                            defaults[ChannelSheetCol.RUN3_STATUS] ?? '';
                        newChannel.fcuData.motorChannelSetting.runStatusString4 =
                            defaults[ChannelSheetCol.RUN4_STATUS] ?? '';
                        newChannel.fcuData.motorChannelSetting.runStatusString5 =
                            defaults[ChannelSheetCol.RUN5_STATUS] ?? '';
                    }
                }
            }
        } else if (event.name === 'motorStopStatusString') {
            if (newChannel.fcuData.motorChannelSetting) {
                newChannel.fcuData.motorChannelSetting.stopStatusString = event.value;
            }
        } else if (event.name === 'motorStbyStatusString') {
            if (newChannel.fcuData.motorChannelSetting) {
                newChannel.fcuData.motorChannelSetting.stbyStatusString = event.value;
            }
        } else if (event.name === 'motorRunStatusString1') {
            if (newChannel.fcuData.motorChannelSetting) {
                newChannel.fcuData.motorChannelSetting.runStatusString1 = event.value;
            }
        } else if (event.name === 'motorRunStatusString2') {
            if (newChannel.fcuData.motorChannelSetting) {
                newChannel.fcuData.motorChannelSetting.runStatusString2 = event.value;
            }
        } else if (event.name === 'motorRunStatusString3') {
            if (newChannel.fcuData.motorChannelSetting) {
                newChannel.fcuData.motorChannelSetting.runStatusString3 = event.value;
            }
        } else if (event.name === 'motorRunStatusString4') {
            if (newChannel.fcuData.motorChannelSetting) {
                newChannel.fcuData.motorChannelSetting.runStatusString4 = event.value;
            }
        } else if (event.name === 'motorRunStatusString5') {
            if (newChannel.fcuData.motorChannelSetting) {
                newChannel.fcuData.motorChannelSetting.runStatusString5 = event.value;
            }
        } else if (event.name === 'measurementUnit') {
            newChannel.opsData.measurementUnit = event.value;
        } else if (event.name.includes('normalRange')) {
            this.isEditingRange = true;
            if (!this.isNormalRangeEnabled) {
                this.isNormalRangeEnabled = true;
                this.cdr.detectChanges();
            }
            const inputString = String(event.value ?? '');
            const rangeValue = inputString.trim() === '' ? NaN : Number(inputString);
            if (event.name.includes('Min')) {
                newChannel.fcuData.normalRange.min = rangeValue;
                this.normalRangeMinString = inputString;
            } else {
                newChannel.fcuData.normalRange.max = rangeValue;
                this.normalRangeMaxString = inputString;
            }
        } else if (event.name.includes('measuringRange')) {
            this.isEditingRange = true;
            const inputString = String(event.value ?? '');
            const rangeValue = inputString.trim() === '' ? NaN : Number(event.value);
            if (event.name.includes('Min')) {
                newChannel.fcuData.measurableRange.min = rangeValue;
                this.measurableRangeMinString = inputString;
            } else {
                newChannel.fcuData.measurableRange.max = rangeValue;
                this.measurableRangeMaxString = inputString;
            }
            newChannel.fcuData.scale = this.computeMeasurableScale();
        } else if (event.property) {
            if (event.name === 'manualReposeFlag') {
                newChannel.fcuData[event.property][event.name] = event.value == '1';
            } else if (event.name === 'normalOpenFlag') {
                newChannel.fcuData[event.property][event.name] = Boolean(event.value);
            } else if (event.name === 'enable') {
                const channelType = newChannel.fcuData.channelType;
                if (isSystemChannel(channelType)) {
                    newChannel.fcuData[event.property][event.name] = event.value;
                } else {
                    const newSetting = AlarmSettingFns.createDefaultSetting(
                        event.value,
                        channelType,
                        event.property as any,
                        newChannel.fcuData[event.property]
                    );
                    newChannel.fcuData[event.property] = newSetting;
                }
            } else {
                if (event.name === 'motor') {
                    newChannel.fcuData.motorChannelSetting[event.property] = event.value;
                } else if (event.name === 'statusString') {
                    newChannel.fcuData[event.property][event.name] = event.value;
                } else {
                    newChannel.fcuData[event.property][event.name] = Number(event.value);
                }
            }
        }

        this.updateChannelTypeFlags(newChannel);
        this.newChannel = newChannel;

        if (isChannelTypeChanged) this.syncRangeStrings();

        if (!this.isSaveOn) {
            this.channelSheetService.setIsChannelUpdated(!_.isEqual(this.initialChannelValues, this.newChannel));
        }

        this.channelSheetService.setIsAlarmUpdate(!_.isEqual(this.initialChannelValues, this.newChannel));
        this.channelSheetService.setUpdateChannel(this.newChannel);
    }

    updateNormalRangeEnable(event: { checked: boolean }) {
        this.isNormalRangeEnabled = event.checked;

        if (!this.isNormalRangeEnabled) {
            this.savedNormalRange = _.cloneDeep(this.newChannel.fcuData.normalRange);
            const channelForSheet = _.cloneDeep(this.newChannel);
            channelForSheet.fcuData.normalRange.min = NaN;
            channelForSheet.fcuData.normalRange.max = NaN;
            this.newChannel.fcuData.normalRange.min = NaN;
            this.newChannel.fcuData.normalRange.max = NaN;
            this.syncRangeStrings();
            this.channelSheetService.setUpdateChannel(channelForSheet);
        } else {
            if (this.savedNormalRange) {
                this.newChannel.fcuData.normalRange = this.savedNormalRange;
                this.syncRangeStrings();
            }
            this.channelSheetService.setUpdateChannel(this.newChannel);
        }

        if (!this.isSaveOn) {
            this.channelSheetService.setIsChannelUpdated(true);
        }
        this.channelSheetService.setIsAlarmUpdate(true);
    }

    toggleAlarmForms() {
        this.isShowSelect = !this.isShowSelect;
    }

    onNumberValidation(value: string) {
        if (value == null || String(value).trim() === '') {
            return {
                state: InputValueState.init,
                message: '',
            };
        }

        if (!/^-?\d+(\.\d+)?$/.test(value)) {
            return {
                state: InputValueState.error,
                message: 'input-text.errors.NOT_NUMBER_TYPE',
            };
        }

        return {
            state: InputValueState.init,
            message: '',
        };
    }

    onNormalRangeValidation = (value: string) => {
        const baseResult = this.onNumberValidation(value);
        if (baseResult.state === InputValueState.error) {
            return baseResult;
        }
        if (value == null || String(value).trim() === '') {
            return baseResult;
        }

        const scale = this.newChannel?.fcuData?.scale || 1;
        const allowedDecimals = scale <= 1 ? 0 : Math.round(Math.log10(scale));
        const decimals = this.countDecimals(value);
        if (decimals > allowedDecimals) {
            return {
                state: InputValueState.error,
                message: 'input-text.errors.DECIMAL_PRECISION',
            };
        }

        return baseResult;
    };

    private formatNormalRangeToMeasurableScale() {
        if (!this.newChannel?.fcuData) return;

        if (this.measurableRangeMinString || this.measurableRangeMaxString) {
            this.newChannel.fcuData.scale = this.computeMeasurableScale();
        }

        const scale = this.newChannel.fcuData.scale || 1;
        const normalRange = this.newChannel.fcuData.normalRange;
        if (normalRange) {
            if (!Number.isNaN(normalRange.min)) {
                this.normalRangeMinString = this.formatWithScale(normalRange.min, scale);
            }
            if (!Number.isNaN(normalRange.max)) {
                this.normalRangeMaxString = this.formatWithScale(normalRange.max, scale);
            }
        }

        this.formatSetPointsToScale(scale);
        this.newChannel = _.cloneDeep(this.newChannel);
        this.cdr.detectChanges();
    }

    private formatSetPointsToScale(scale: number) {
        const fcuData = this.newChannel?.fcuData as any;
        if (!fcuData) return;

        const alarmSettingKeys = [
            AlarmPropertyList.alarmSettingVeryHigh,
            AlarmPropertyList.alarmSettingHigh,
            AlarmPropertyList.alarmSettingLow,
            AlarmPropertyList.alarmSettingVeryLow,
            AlarmPropertyList.alarmSettingFeedbackError,
        ];

        for (const key of alarmSettingKeys) {
            const setting = fcuData[key];
            if (!setting || setting.setPoint == null || Number.isNaN(setting.setPoint)) continue;
            const formatted = this.formatWithScale(setting.setPoint, scale);
            if (formatted !== '') {
                setting.setPoint = Number(formatted);
            }
        }
    }

    handleFocusChanged() {
        this.isEditingRange = false;
        this.formatNormalRangeToMeasurableScale();

        const hasChanged = !_.isEqual(this.initialChannelValues, this.newChannel);
        if (!this.isSaveOn) {
            this.channelSheetService.setIsChannelUpdated(hasChanged);
        }
        this.channelSheetService.setIsAlarmUpdate(hasChanged);
        if (hasChanged) {
            this.channelSheetService.setUpdateChannel(this.newChannel);
        }
    }

    handleContactChange() {
        if (!this.newChannel?.fcuData?.digitalAlarmSetting) return;
        const setting = this.newChannel.fcuData.digitalAlarmSetting;
        setting.statusString = `${setting.statusStringContactOn ?? ''}/${setting.statusStringContactOff ?? ''}`;
        this.channelSheetService.setUpdateChannel(this.newChannel);
    }

    handleSwapStatus() {
        if (!this.newChannel?.fcuData?.digitalAlarmSetting) return;

        const setting = this.newChannel.fcuData.digitalAlarmSetting;
        [setting.statusStringContactOn, setting.statusStringContactOff] = [
            setting.statusStringContactOff,
            setting.statusStringContactOn,
        ];
        setting.statusString = `${setting.statusStringContactOn ?? ''}/${setting.statusStringContactOff ?? ''}`;

        this.channelSheetService.setIsAlarmUpdate(!_.isEqual(this.selectedChannel, this.newChannel));
        this.channelSheetService.setUpdateChannel(this.newChannel);
    }

    isChannelBlank() {
        return isBlank(this.newChannel?.opsData?.channelType);
    }

    get isExhGas(): boolean {
        return this.isExhGasDev || this.isExhGasMean;
    }

    get sliderStep(): number {
        const scale = this.newChannel?.fcuData?.scale || 1;
        const scaleDecimals = scale === 1 ? 0 : Math.round(Math.log10(scale));
        const min = this.newChannel?.fcuData?.measurableRange?.min;
        const max = this.newChannel?.fcuData?.measurableRange?.max;
        const valueDecimals = Math.max(this.getDecimalPlaces(min), this.getDecimalPlaces(max));
        const decimals = Math.max(scaleDecimals, valueDecimals);
        return decimals === 0 ? 1 : Math.pow(10, -decimals);
    }

    private countDecimals(str: string): number {
        const dotIndex = str.indexOf('.');
        return dotIndex === -1 ? 0 : str.length - dotIndex - 1;
    }

    private getDecimalPlaces(value: number): number {
        if (value == null || !isFinite(value)) return 0;
        return this.countDecimals(value.toString());
    }

    private updateChannelTypeFlags(channel: Channel) {
        if (!channel) return;
        const channelType = channel.fcuData?.channelType ?? ChannelTypeStringLookup[channel.opsData?.channelType];

        this.isAnalog = getAnalogTypesSet().has(channelType);
        this.isRunning = getRunningTypesSet().has(channelType);
        this.isFlow = getFlowTypesSet().has(channelType);
        this.isExhGasDev = channelType === ChannelType.EXH_GAS_DEV;
        this.isExhGasMean = channelType === ChannelType.EXH_GAS_MEAN;
        this.isDigitalContact = channelType === ChannelType.DIGITAL_CONTACT;
        this.isDigitalMotor = channelType === ChannelType.DIGITAL_MOTOR;
        this.isSystemChannel = channelType === ChannelType.SYSTEM;
    }
}
